import "./instrument";
import { StrictMode, lazy, Suspense } from 'react'

// Suppress safe-to-ignore library-internal deprecation warnings (e.g. THREE.Clock in React Three Fiber)
const originalWarn = console.warn;
console.warn = (...args) => {
  if (args[0] && typeof args[0] === 'string' && args[0].includes('THREE.THREE.Clock')) {
    return;
  }
  originalWarn(...args);
};

import { createRoot } from 'react-dom/client'
import * as Sentry from "@sentry/react";
import './index.css'
import './i18n'
import App from './App'
import { CrashFallback } from './components/CrashFallback';
import { useAuthStore } from './store/useAuthStore';
import { useStorageStore } from './store/useStorageStore';
import { useDanceStore } from './store/useDanceStore';
import { TermsUpdateModal } from './components/TermsUpdateModal';
import { ResponsiveProvider } from './contexts/ResponsiveContext';

// Initialize Auth listener
useAuthStore.getState().initialize();

// Dev-only: expose stores so Playwright tests can seed Zustand state directly.
if (import.meta.env.DEV) {
  (window as any).__TEST_STORES__ = { useStorageStore, useDanceStore };
}

const UserManual = lazy(() => import('./components/UserManual'));
const TermsOfUseModule = lazy(() => import('./components/TermsOfUseModule').then(m => ({ default: m.TermsOfUseModule })));
const PrivacyPolicyModule = lazy(() => import('./components/PrivacyPolicyModule').then(m => ({ default: m.PrivacyPolicyModule })));
const ShowcaseSequencesModule = lazy(() => import('./components/ShowcaseSequencesModule').then(m => ({ default: m.ShowcaseSequencesModule })));
const PlansModule = lazy(() => import('./components/PlansModule').then(m => ({ default: m.PlansModule })));
const SharedSoundtracksModule = lazy(() => import('./components/SharedSoundtracksModule').then(m => ({ default: m.SharedSoundtracksModule })));

const isUserManual = window.location.pathname === '/usermanual' || window.location.pathname.startsWith('/usermanual/');
const isTermsOfUse = window.location.pathname === '/termsofuse' || window.location.pathname.startsWith('/termsofuse/');
const isPrivacy = window.location.pathname === '/privacy' || window.location.pathname.startsWith('/privacy/');
const isShowcase = window.location.pathname === '/showcase' || window.location.pathname.startsWith('/showcase/');
const isPlans = window.location.pathname === '/plans' || window.location.pathname.startsWith('/plans/');
const isSharedSoundtracks = window.location.pathname === '/sharedsoundtracks' || window.location.pathname.startsWith('/sharedsoundtracks/');

const container = document.getElementById('root');
if (container) {
  createRoot(container, {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  }).render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={(props) => <CrashFallback {...props} />}>
        {isUserManual ? (
          <Suspense fallback={
            <div style={{
              background: '#0f172a',
              color: '#38bdf8',
              height: '100vh',
              width: '100vw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'sans-serif',
              fontWeight: 600
            }}>
              Loading Manual...
            </div>
          }>
            <UserManual />
          </Suspense>
        ) : isTermsOfUse ? (
          <Suspense fallback={
            <div style={{
              background: '#0f172a',
              color: '#38bdf8',
              height: '100vh',
              width: '100vw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'sans-serif',
              fontWeight: 600
            }}>
              Loading Terms of Use...
            </div>
          }>
            <TermsOfUseModule />
          </Suspense>
        ) : isPrivacy ? (
          <Suspense fallback={
            <div style={{
              background: '#0f172a',
              color: '#38bdf8',
              height: '100vh',
              width: '100vw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'sans-serif',
              fontWeight: 600
            }}>
              Loading Privacy Policy...
            </div>
          }>
            <PrivacyPolicyModule />
          </Suspense>
        ) : isPlans ? (
          <Suspense fallback={
            <div style={{
              background: '#0f172a',
              color: '#38bdf8',
              height: '100vh',
              width: '100vw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'sans-serif',
              fontWeight: 600
            }}>
              Loading Plans...
            </div>
          }>
            <PlansModule />
          </Suspense>
        ) : isSharedSoundtracks ? (
          <Suspense fallback={
            <div style={{
              background: '#0f172a',
              color: '#38bdf8',
              height: '100vh',
              width: '100vw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'sans-serif',
              fontWeight: 600
            }}>
              Loading...
            </div>
          }>
            <SharedSoundtracksModule />
          </Suspense>
        ) : isShowcase ? (
          <Suspense fallback={
            <div style={{
              background: '#0f172a',
              color: '#a855f7',
              height: '100vh',
              width: '100vw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'sans-serif',
              fontWeight: 600
            }}>
              Loading Showcase...
            </div>
          }>
            <ShowcaseSequencesModule />
          </Suspense>
        ) : (
          <ResponsiveProvider>
            <App />
            <TermsUpdateModal />
          </ResponsiveProvider>
        )}
      </Sentry.ErrorBoundary>
    </StrictMode>,
  )
}

